import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { EmployerHubLayout } from "../../components/hub/EmployerHubLayout";
import { HubEmptyPanel } from "../../components/hub/HubEmptyPanel";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Briefcase, Users, CalendarClock, ArrowRight, Check, Clock } from "lucide-react";

const STEPS = ["Intake received", "In progress", "Candidates ready", "Interviews underway"];

const StageTracker = ({ progressStep }) => (
  <div className="mt-8" data-testid="hub-stage-tracker">
    <div className="flex items-start">
      {STEPS.map((label, i) => {
        const done = i < progressStep;
        const current = i === progressStep;
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <div
                className={`flex-1 h-px mt-3.5 ${i <= progressStep - 1 ? "bg-blue-600" : "bg-gray-200"}`}
              />
            )}
            <div className="flex flex-col items-center w-20 sm:w-28" data-testid={`hub-stage-step-${i}`}>
              <span
                className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold border ${
                  done
                    ? "bg-blue-600 border-blue-600 text-white"
                    : current
                    ? "bg-white border-blue-600 text-blue-700"
                    : "bg-white border-gray-300 text-gray-400"
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span
                className={`mt-2 text-[11px] sm:text-xs text-center leading-tight ${
                  done || current ? "text-gray-900 font-medium" : "text-gray-400"
                }`}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export default function EmployerHubOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/api/employer/hub/overview")
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || "Unable to load your workspace"));
  }, []);

  return (
    <EmployerHubLayout>
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3" data-testid="hub-overview-error">
          {error}
        </div>
      )}

      {!data && !error && (
        <div className="space-y-6" data-testid="hub-overview-loading">
          <div className="h-8 w-72 bg-gray-100 rounded animate-pulse" />
          <div className="h-52 bg-gray-100 rounded-2xl animate-pulse" />
        </div>
      )}

      {data && (
        <div data-testid="employer-hub-overview">
          {/* Greeting — Gate 1 Revision 4: fixed "Welcome back" */}
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="hub-greeting">
            Welcome back, {data.first_name}.
          </h1>
          <p className="mt-2 text-gray-500" data-testid="hub-greeting-subline">
            Here&apos;s what&apos;s happening with your Inter-View service.
          </p>

          {!data.active_service ? (
            <div className="mt-10">
              <HubEmptyPanel
                icon={Briefcase}
                testid="hub-overview-empty"
                title="Your workspace is ready"
                body="When you begin an Employer Intake Package, your service progress, candidates, and interviews will all live here — one calm place to follow everything."
                action={
                  <Link to="/employers/intake">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="hub-empty-cta">
                      Start with the Employer Intake Package
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                }
              />
            </div>
          ) : (
            <>
              {/* Active service card */}
              <div
                className="mt-10 bg-white border border-gray-200 rounded-2xl p-6 sm:p-8"
                data-testid="hub-active-service-card"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className="text-lg font-semibold text-gray-900" data-testid="hub-service-employer-name">
                        {data.active_service.employer_name}
                      </h2>
                      {data.active_service.is_seed && (
                        <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50" data-testid="hub-seed-badge">
                          Test data
                        </Badge>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500" data-testid="hub-service-package-line">
                      {data.active_service.package_name} · Ref {data.active_service.service_reference}
                    </p>
                    {data.active_service.created_at && (
                      <p className="mt-0.5 text-sm text-gray-400" data-testid="hub-service-date">
                        Purchased {formatDate(data.active_service.created_at)}
                      </p>
                    )}
                  </div>
                  {data.active_service.stage === "payment_pending" && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-gray-500" data-testid="hub-payment-pending-note">
                      <Clock className="h-4 w-4" /> Payment processing…
                    </span>
                  )}
                </div>

                {data.active_service.stage !== "inquiry" && (
                  <StageTracker progressStep={data.active_service.progress_step} />
                )}
                {data.active_service.stage === "inquiry" && (
                  <p className="mt-6 text-sm text-gray-500" data-testid="hub-inquiry-note">
                    Your inquiry has been received. Our team will be in touch shortly.
                  </p>
                )}
              </div>

              {/* At a glance + next step */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="hub-at-a-glance">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">At a glance</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Users className="h-4 w-4 text-blue-600" />
                      </span>
                      <span data-testid="hub-candidates-count">
                        <span className="font-semibold text-gray-900">{data.counts.candidates}</span>{" "}
                        candidate{data.counts.candidates === 1 ? "" : "s"} presented
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <CalendarClock className="h-4 w-4 text-blue-600" />
                      </span>
                      <span data-testid="hub-interviews-count">
                        <span className="font-semibold text-gray-900">{data.counts.interviews}</span>{" "}
                        interview{data.counts.interviews === 1 ? "" : "s"} requested
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col justify-between" data-testid="hub-next-step">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Next step</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {data.counts.candidates > 0
                        ? `${data.counts.candidates} candidate${data.counts.candidates === 1 ? " is" : "s are"} waiting in your workspace.`
                        : "Your candidates are being prepared — we'll present them here when they're ready."}
                    </p>
                  </div>
                  {data.counts.candidates > 0 && (
                    <Link to="/employers/hub/candidates" className="mt-4">
                      <Button variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="hub-view-candidates-cta">
                        View candidates
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </EmployerHubLayout>
  );
}
